import PropTypes from "prop-types";
import {ButtonPill, Kicker} from "@/base";
import {useState, useRef, useEffect, useCallback} from "react";
import CmsImageLoader from "@/base/CmsImageLoader";
import useBreakpoints from "@util/breakpoints";
import {css, useTheme} from "@emotion/react";
import Header from "../../base/Header";

import CarouselNav from "@/blades/carouselBlade2/carouselNav";
import CarouselVideo from "@/base/CarouselVideo";

const CarouselBlade2 = ({entity}) => {

    const [activeCarousel, setActiveCarousel] = useState(0);
    const activeCarouselRef = useRef(0)
    const [inView, setInView] = useState(false);
 
    const hasEnded = useRef(false);
    const copyWidth = 390;
    const aspectRatios = useRef(['16/9'])

    const slideTime = 4000;

    const carouselWrapper = useRef();
    const slideCount = useRef(0);
    const slideTimer = useRef();
    const remainingTime = useRef(slideTime)
    const startTime = useRef()

    const mediaType = useRef();

    const isElementInViewport = useCallback(() => {

        let rect = carouselWrapper.current.getBoundingClientRect();
        let windowHeight = (window.innerHeight || document.documentElement.clientHeight);

        let margin = 200; // may look into adding value of data-aos-offset if used

        let hasEntered = (
            rect.top < (windowHeight-margin) && rect.bottom > margin
        );

        if(inView !== hasEntered ){
            setInView(hasEntered);
        }

    }, [setInView, inView]);

    /** Add your event listeners to detect if element is in view **/

    useEffect(()=>{

        window.addEventListener('scroll', isElementInViewport);
        isElementInViewport();
        return ()=>window.removeEventListener('scroll', isElementInViewport);

    },[isElementInViewport]);

    const pauseTimer = () => {

        clearTimeout(slideTimer.current)
        if(startTime.current) {
            remainingTime.current -= Date.now() - startTime.current;
        } else {
            remainingTime.current = slideTime
        }

    }

    const startTimer = useCallback(() => {

        startTime.current = Date.now();
        slideTimer.current = setTimeout(()=>{
            imageSlideUpdate()
        }, remainingTime.current)

    }, [imageSlideUpdate]);

    const restartTimer = useCallback(() => {

        if(slideTimer.current) {

            remainingTime.current = slideTime;
            startTime.current = null;
            clearTimeout(slideTimer.current)

        }

        startTimer()

    }, [startTimer])

    useEffect(()=>{

        // view change, start or pause timer
        if(inView) {
            if(mediaType.current === 'image') startTimer();
        }  else {
            if(mediaType.current === 'image')  pauseTimer();
        }

    }, [inView, startTimer])

    useEffect(()=>{

        // carousel change, full restart image timer
        if(activeCarouselRef.current !== activeCarousel){
            if(mediaType.current === 'image') restartTimer()
            activeCarouselRef.current = activeCarousel;
            hasEnded.current = false;
        }

    }, [activeCarousel, restartTimer])

    const changeSlide = ()=> {
        setActiveCarousel((current=>{
            return current === slideCount.current-1 ? 0 : current+1
        }))
    }

    const videoEndUpdate = () => {
        
        changeSlide();
        hasEnded.current = true;
    }

    const imageSlideUpdate = useCallback(() => {
        changeSlide()
    }, [])

    const theme = useTheme();

    const mediaFlex = useBreakpoints([theme.breakpoint.md])({
        width: ['100%', `calc(100% - ${copyWidth}px)`],
        order: 1,
        // minHeight: size,
        position: 'relative',
        background: '#000',
        // 'img, iframe': {
        'iframe': {
            position: 'absolute',
            top: '0',
            width:'100%',
            height:'100%',
            objectFit: 'cover',
        }
    })

    const fillCopy = useBreakpoints([theme.breakpoint.md])({
        position: 'relative',
        pointerEvents: 'none'
    })

 

    

    const renderMedia = (el, slideIndex) => {

        if(slideIndex === activeCarousel) mediaType.current = el.render_name;

        let media;

        let fillPosition = css(`
            position: absolute;
            height: 100%;
            width: 100%;
            display: flex;

            img, video {
                width: 100%;
                height: auto;
            }
        `)
            
        if(el.render_name === 'video_url'){

            const videoLoaded = (width, height) => {
                aspectRatios.current[slideIndex] = `${width}/${height}`
            }

            media = (
                <CarouselVideo 
                video_id={el.value} 
                video_provider={'raw_url'} 
                autoplay={true}
                background={true} 
                onVideoEnd={videoEndUpdate}
                onVideoLoad={videoLoaded}
                paused={!inView || slideIndex !== activeCarousel} 
                activate={slideIndex === activeCarousel}/>
            )
        }

        if(el.render_name === 'video'){

            //IFRAMES NEED MORE
            aspectRatios.current[slideIndex] = `16/9`

            fillPosition = {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                width: '100%',
                height: '100%'
            }

            let video_id = el.value.elements.find(el=>el.render_name==='video_id');
            let video_provider = el.value.elements.find(el=>el.render_name==='video_provider');

            media = (
                <CarouselVideo video_id={video_id.value} video_provider={video_provider.value} autoplay={true} background={false}/>
            )
        }

        if(el.render_name === 'image'){

            

            let image_url = el.value.elements.find(el=>el.render_name==='image_url');
            let alt_text = el.value.elements.find(el=>el.render_name==='alt_text');

            const dimensions = (width, height) => {
                aspectRatios.current[slideIndex] = `${width}/${height}`
            }

            media = (
                <CmsImageLoader url={image_url.value} alt={alt_text.value} dimensions={dimensions} />
            )
        }

        let animateClass = slideIndex === activeCarousel ? 'fade-in' : 'fade-out';

        return (
            <div key={el.id} css={fillPosition} className={`fade-animate ${animateClass}`}>
                {media}
            </div>
        )

    }


    const renderButton = (button) => {
        if(button){

            let buttonUrl = button.value.elements.find(el=>el.render_name==='button_url');
            let buttonText = button.value.elements.find(el=>el.render_name==='button_text');
            let buttonTheme = button.value.elements.find(el=>el.render_name==='button_theme');

            return (
                <ButtonPill to={buttonUrl.value} buttonTheme={buttonTheme.value} stretch={true}>{buttonText.value}</ButtonPill>
            )
        }
    }

    const copyStyle = useBreakpoints([theme.breakpoint.md])({
        minWidth: ['100%', `${copyWidth}px`],
        width: ['100%', `${copyWidth}px`],
        order: ["3","2"],
        position: 'relative',
        // minHeight: ['100%', '660px']
    })

    const renderContent = (parent, slideIndex) => {

        let kicker = parent.value.elements.find(el=>el.render_name==='kicker');
        let title = parent.value.elements.find(el=>el.render_name==='title');
        let copy = parent.value.elements.find(el=>el.render_name==='product_info');
        let button = parent.value.elements.find(el=>el.render_name==='call_to_action');

        let animateClass = slideIndex === activeCarousel ? 'fade-in' : 'fade-out';

        return (
            <div key={parent.id} css={fillCopy} className={`d-flex flex-column px-2 px-sm-4 py-4 fade-copy ${animateClass}`}>

                {kicker && <Kicker key={`kicker_${parent.id}`}>{kicker.value}</Kicker>}

                {title && <Header tag="h3">{title.value}</Header>}

                {copy && <div dangerouslySetInnerHTML={{__html: copy.value}}/> }

                {renderButton(button)}

            </div>
        )
    }

    

    let renderSlides= () => {

        let slides = entity.value.elements.filter(el=>el.render_name!=='gated_flags');

        slideCount.current = 0

        if(slides) {

            let newSlides = slides.map((slide, slideIndex)=>{

                slideCount.current++;

                let mediaItem = slide.value.elements.find(item=>{
                    if(item.render_name === 'video_url' || item.render_name === 'image' || item.render_name === 'video') return item;
                });

                return {
                    copy: renderContent(slide, slideIndex),
                    media: renderMedia(mediaItem, slideIndex)
                }
            })

            return (
                <div ref={carouselWrapper} className={'d-flex flex-column flex-md-row flex-wrap pb-4'} /*css={height}*/>

                    <div css={mediaFlex} className={'overflowHidden'} style={{aspectRatio: aspectRatios.current[activeCarousel]}}
                        //  onMouseOver={()=>pauseCarousel(true)}
                        //  onFocus={()=>pauseCarousel(true)}
                        //  onMouseOut={()=>pauseCarousel(false)}
                        //  onBlur={()=>pauseCarousel(false)}
                        >
                        {newSlides.map((media)=>media.media)}
                    </div>

                    <CarouselNav slides={slides} activeCarousel={activeCarousel} setActiveCarousel={setActiveCarousel}/>

                    <div css={copyStyle}
                        //  onMouseOver={()=>pauseCarousel(true)}
                        //  onFocus={()=>pauseCarousel(true)}
                        //  onMouseOut={()=>pauseCarousel(false)}
                        //  onBlur={()=>pauseCarousel(false)}
                         >
                        {newSlides.map((copy)=>copy.copy)}
                    </div>

                </div>

            )
        }
    }

    return renderSlides()
};

CarouselBlade2.propTypes = {
    entity: PropTypes.object
};

export default CarouselBlade2;
